import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import Privacy from "@/pages/Privacy";
import Fees from "@/pages/Fees";
import Help from "@/pages/Help";
import Dashboard from "@/pages/Dashboard";
import Invest from "@/pages/Invest";
import Insights from "@/pages/Insights";
import LivePortfolios from "@/pages/LivePortfolios";
import TradeGlobal from "@/pages/TradeGlobal";
import WorldMarkets from "@/pages/WorldMarkets";
import StockDetail from "@/pages/StockDetail";
import TradeGCC from "@/pages/TradeGCC";
import TradeAssets from "@/pages/TradeAssets";
import Save from "@/pages/Save";
import Murabaha from "@/pages/Murabaha";
import Broker from "@/pages/Broker";
import Orders from "@/pages/Orders";
import Alerts from "@/pages/Alerts";
import Halal from "@/pages/Halal";
import Zakat from "@/pages/Zakat";
import Dividends from "@/pages/Dividends";
import ShariahWatch from "@/pages/ShariahWatch";
import Learn from "@/pages/Learn";
import ShariahBoard from "@/pages/ShariahBoard";
import HalalScreener from "@/pages/HalalScreener";
import HalalStock from "@/pages/HalalStock";
import Leaderboards from "@/pages/Leaderboards";
import ChartLens from "@/pages/ChartLens";
import Product from "@/pages/Product";
import Exchanges from "@/pages/Exchanges";
import Portfolios from "@/pages/Portfolios";
import ProviderDeck from "@/pages/ProviderDeck";
import Article from "@/pages/Article";
import Wallet from "@/pages/Wallet";
import PaymentReturn from "@/pages/PaymentReturn";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";

const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useApp();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading Tharwy...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // New members must finish the signup questionnaire first (existing accounts are unaffected).
  if (user.onboarding_completed === false && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

// Onboarding: only for signed-in members who haven't completed it yet.
const OnboardingRoute = () => {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading Tharwy...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_completed !== false) return <Navigate to="/" replace />;
  return <Onboarding />;
};

const Public = ({ children }) => {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading Tharwy...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Logged-out visitors see the marketing landing page at "/"; members see the dashboard.
const Home = () => {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading Tharwy...</div>;
  if (user && user.onboarding_completed === false) return <Navigate to="/onboarding" replace />;
  return user ? <Dashboard /> : <Landing />;
};

function AppRoutes() {
  const location = useLocation();
  const { displayCcy } = useApp();
  // Intercept the OAuth redirect (#session_id=...) BEFORE any route/guard runs.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes key={displayCcy}>
      <Route path="/login" element={<Public><Login /></Public>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Home />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/fees" element={<Fees />} />
      <Route path="/help" element={<Help />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/shariah-board" element={<ShariahBoard />} />
      <Route path="/top-halal-stocks" element={<HalalScreener />} />
      <Route path="/halal-stocks/:symbol" element={<HalalStock />} />
      <Route path="/halal-leaderboards" element={<Leaderboards />} />
      <Route path="/chart-lens" element={<ChartLens />} />
      <Route path="/learn/:slug" element={<Article />} />
      <Route path="/product/:slug" element={<Product />} />
      <Route path="/exchanges" element={<Exchanges />} />
      <Route path="/portfolios" element={<Portfolios />} />
      <Route path="/deck/providers" element={<ProviderDeck />} />
      <Route path="/invest" element={<Protected><Invest /></Protected>} />
      <Route path="/insights" element={<Protected><Insights /></Protected>} />
      <Route path="/live" element={<Protected><LivePortfolios /></Protected>} />
      <Route path="/markets" element={<Navigate to="/trade-global" replace />} />
      <Route path="/trade-global" element={<Protected><TradeGlobal /></Protected>} />
      <Route path="/trade-world" element={<Protected><WorldMarkets region="world" /></Protected>} />
      <Route path="/trade-eu" element={<Protected><WorldMarkets region="eu" /></Protected>} />
      <Route path="/trade-asia" element={<Protected><WorldMarkets region="asia" /></Protected>} />
      <Route path="/stock/:symbol" element={<Protected><StockDetail /></Protected>} />
      <Route path="/trade-gcc" element={<Protected><TradeGCC /></Protected>} />
      <Route path="/trade-commodities" element={<Protected><TradeAssets assetClass="commodity" title="Commodities" subtitle="Metals, energy and agriculture — bulk commodities quoted in USD per metric tonne (USD/MT)" /></Protected>} />
      <Route path="/trade-forex" element={<Protected><TradeAssets assetClass="forex" title="Forex" subtitle="Trade major, minor and GCC currency pairs" /></Protected>} />
      <Route path="/trade-crypto" element={<Protected><TradeAssets assetClass="crypto" title="Crypto" subtitle="Trade Bitcoin, Ethereum and major digital assets · priced in USD" /></Protected>} />
      <Route path="/save" element={<Protected><Save /></Protected>} />
      <Route path="/murabaha" element={<Protected><Murabaha /></Protected>} />
      <Route path="/halal" element={<Protected><Halal /></Protected>} />
      <Route path="/zakat" element={<Protected><Zakat /></Protected>} />
      <Route path="/dividends" element={<Protected><Dividends /></Protected>} />
      <Route path="/shariah-watch" element={<Protected><ShariahWatch /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/broker" element={<Protected><Broker /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentReturn /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentReturn cancelled /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AppProvider>
    </div>
  );
}

export default App;
